import { useEffect, useState } from "react";
import dayjs from "dayjs";
import type { DateRange } from "../components/DurationSelector/types";

const STORAGE_KEY = "peak4.admin.dateRange.v1";
const STORAGE_FORMAT = "YYYY-MM-DD";

const buildDefaultRange = (): DateRange => [
  dayjs().startOf("month"),
  dayjs().endOf("month"),
];

const parseDate = (value: unknown) => {
  if (typeof value !== "string") return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const loadRange = (key: string): DateRange | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { start?: string | null; end?: string | null };
    const start = parseDate(parsed?.start);
    const end = parseDate(parsed?.end);
    if (!start && !end) return null;
    return [start, end];
  } catch {
    return null;
  }
};

export const usePersistedDateRange = (options?: {
  storageKey?: string;
  defaultRange?: DateRange;
}) => {
  const storageKey = options?.storageKey ?? STORAGE_KEY;
  const fallbackRange = options?.defaultRange ?? buildDefaultRange();

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    return loadRange(storageKey) ?? fallbackRange;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const payload = {
      start: dateRange[0]?.format(STORAGE_FORMAT) ?? null,
      end: dateRange[1]?.format(STORAGE_FORMAT) ?? null,
    };
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch {
      // ignore storage errors (e.g. disabled cookies/storage)
    }
  }, [dateRange, storageKey]);

  return { dateRange, setDateRange } as const;
};
